import { defineStore } from 'pinia';
import { api, erebus } from 'boot/axios';

/**
 * Connection status representing the health of a backend service.
 *
 * - `online`: All requests succeeding, no issues detected
 * - `degraded`: Some failures detected, connection unstable but partially working
 * - `offline`: Multiple consecutive failures, service considered unavailable
 */
export type ConnectionStatus = 'online' | 'degraded' | 'offline';

/**
 * Identifier for the backend services.
 *
 * - `legacy`: The legacy Java API (used for existing functionality)
 * - `erebus`: The new Go API (primary backend for new features)
 */
export type BackendId = 'legacy' | 'erebus';

/**
 * Why a request failed.
 *
 * - `unreachable`: no response at all (connection refused, DNS, offline)
 * - `timeout`: the socket opened but the request exceeded its timeout
 *
 * The distinction matters because a timeout means the backend answered
 * slowly, not that it is unreachable — see `recordFailure`.
 */
export type FailureKind = 'unreachable' | 'timeout';

/** A single recorded failure within the sliding window. */
interface Failure {
  /** Timestamp (ms) the failure occurred */
  at: number;
  /** What kind of failure it was */
  kind: FailureKind;
}

/**
 * State tracking for an individual backend service.
 */
interface BackendState {
  /** Current connection status */
  status: ConnectionStatus;
  /** Recent failures within the sliding window */
  failures: Failure[];
  /** Timestamp (ms) of the last successful request, or null if none */
  lastSuccess: number | null;
}

/**
 * Root state for the connection store.
 */
interface ConnectionState {
  /** Connection state per backend service */
  backends: Record<BackendId, BackendState>;
}

/** Sliding window for counting failures (30 seconds) */
const FAILURE_WINDOW_MS = 30_000;

/** Number of failures within window to trigger 'degraded' status */
const FAILURES_FOR_DEGRADED = 2;

/** Number of failures within window to trigger 'offline' status */
const FAILURES_FOR_OFFLINE = 4;

/**
 * Connection store for managing backend service connectivity.
 *
 * Tracks the health of both backend services (Legacy Java API and Erebus Go API)
 * independently, using a state machine to transition between online, degraded,
 * and offline states based on request success/failure patterns.
 *
 * Status transitions are driven reactively by axios interceptors — no polling.
 * Every successful response calls `recordSuccess`; every network error calls
 * `recordFailure`. Use `forceRetry` when the user manually requests a retry.
 *
 * @example
 * ```ts
 * const connectionStore = useConnectionStore()
 *
 * // Check overall app status
 * if (connectionStore.appStatus === 'offline') {
 *   showOfflineIndicator()
 * }
 *
 * // Force a connection retry
 * await connectionStore.forceRetry()
 * ```
 */
export const useConnectionStore = defineStore('connection', {
  // No persistence - always start fresh as online on page load
  persist: false,

  state: (): ConnectionState => ({
    backends: {
      legacy: { status: 'online', failures: [], lastSuccess: null },
      erebus: { status: 'online', failures: [], lastSuccess: null },
    },
  }),

  getters: {
    /**
     * Current connection status of the Legacy Java API.
     * @returns The connection status ('online', 'degraded', or 'offline')
     */
    legacyStatus: (state) => state.backends.legacy.status,

    /**
     * Current connection status of the Erebus Go API.
     * @returns The connection status ('online', 'degraded', or 'offline')
     */
    erebusStatus: (state) => state.backends.erebus.status,

    /**
     * Overall application connection status.
     *
     * Simplified user-facing status:
     * - `online`: Both backends are fully online
     * - `degraded`: At least one backend has issues (degraded or offline)
     * - `offline`: Both backends are offline
     *
     * @returns The overall connection status for user display
     */
    appStatus(): ConnectionStatus {
      const legacyStatus = this.legacyStatus;
      const erebusStatus = this.erebusStatus;

      if (legacyStatus === 'offline' && erebusStatus === 'offline') return 'offline';
      if (legacyStatus !== 'online' || erebusStatus !== 'online') return 'degraded';
      return 'online';
    },

    /**
     * Whether all backends are fully online with no issues.
     * @returns true if appStatus is 'online'
     */
    isFullyOnline(): boolean {
      return this.appStatus === 'online';
    },

    /**
     * Whether both backends are completely offline.
     * @returns true if both legacy and erebus are 'offline'
     */
    isFullyOffline(): boolean {
      return this.legacyStatus === 'offline' && this.erebusStatus === 'offline';
    },

    /**
     * Backwards-compatible getter for existing components using `appStore.isOnline`.
     *
     * Returns true unless the app is fully offline. This maintains compatibility
     * with the ~17 components that check `isOnline` before making requests.
     *
     * @returns true if appStatus is 'online' or 'degraded', false if 'offline'
     */
    isOnline(): boolean {
      return this.appStatus !== 'offline';
    },
  },

  actions: {
    /**
     * Record a successful request for a backend.
     *
     * Clears all recorded failures and resets the backend to 'online' status.
     *
     * Called automatically by axios response interceptors on successful requests.
     *
     * @param backend - The backend that succeeded ('legacy' or 'erebus')
     */
    recordSuccess(backend: BackendId) {
      const state = this.backends[backend];
      state.lastSuccess = Date.now();
      state.failures = [];
      state.status = 'online';
    },

    /**
     * Record a failed request for a backend.
     *
     * Adds the failure to the sliding window, prunes old failures, and
     * transitions the status if failure thresholds are exceeded:
     * - 2+ failures in 30s → 'degraded'
     * - 4+ failures in 30s → 'offline', but only if at least one of them was
     *   `unreachable`
     *
     * Timeouts alone cap at 'degraded'. A backend that answers slowly is still
     * answering, and 'offline' flips `isOnline` to false, which stops components
     * from making requests they could still complete.
     *
     * Called automatically by axios error interceptors on network errors (status 0).
     *
     * @param backend - The backend that failed ('legacy' or 'erebus')
     * @param kind - Why it failed; defaults to 'unreachable'
     */
    recordFailure(backend: BackendId, kind: FailureKind = 'unreachable') {
      const state = this.backends[backend];
      const now = Date.now();

      state.failures.push({ at: now, kind });
      state.failures = state.failures.filter((f) => now - f.at < FAILURE_WINDOW_MS);

      const failureCount = state.failures.length;
      const hasUnreachable = state.failures.some((f) => f.kind === 'unreachable');

      if (failureCount >= FAILURES_FOR_OFFLINE && hasUnreachable) {
        state.status = 'offline';
      } else if (failureCount >= FAILURES_FOR_DEGRADED) {
        state.status = 'degraded';
      }
    },

    /**
     * Force an immediate connection retry for all backends.
     *
     * Pings both backend status endpoints in parallel. Success/failure
     * is recorded via the normal axios interceptors.
     *
     * Use this when the user manually requests a retry (e.g., clicking
     * the offline indicator button).
     */
    async forceRetry() {
      const checks: Promise<void>[] = [];

      const ping = async (backend: BackendId) => {
        try {
          const client = backend === 'legacy' ? api : erebus;
          const endpoint = backend === 'legacy' ? '/api/v2/status' : '/status';
          await client.get(endpoint, { timeout: 5000 });
          // Success/failure recorded by axios interceptors
        } catch {
          // Failure recorded by axios interceptors
        }
      };

      checks.push(ping('legacy'));
      checks.push(ping('erebus'));
      await Promise.all(checks);
    },

    /**
     * Clean up connection store state.
     *
     * Resets the store to its initial state. Called during app reset/logout
     * via `applicationStore.reset()`.
     */
    cleanup() {
      this.$reset();
    },
  },
});
